import { useContext, type ReactElement, type SubmitEvent } from "react";

import { usePageMeta } from "../../app/use-page-meta.js";
import { FormSummary } from "../../forms/fields.js";

import { CardCheckoutContext } from "./card-checkout.js";
import type { Confirmation, ContractKind } from "./contract-types.js";
import type { ContractFlow } from "./use-contract-flow.js";

/** 確認の一覧。<b>表示する文は API が作ったものを並べるだけ</b>。 */
function ConfirmationList({ value, trialNote }: {
    readonly value: Confirmation;
    readonly trialNote: string;
}): ReactElement {
    return (
        <dl className="grid grid-cols-[10rem_1fr] gap-y-2.5 text-sm">
            <dt className="text-slate-500">料金プラン</dt><dd>{value.planDisplayName}</dd>
            <dt className="text-slate-500">利用人数</dt><dd>{value.qty} 名</dd>
            {value.initialStorageSize === null
                ? null
                : <><dt className="text-slate-500">初期ストレージ容量</dt><dd>{value.initialStorageSize} GB</dd></>}
            <dt className="text-slate-500">追加ストレージ容量</dt><dd>{value.storageSize} GB</dd>
            <dt className="text-slate-500">オプション機能</dt><dd>{value.extensionTitles}</dd>
            {value.company === null
                ? null
                : (
                    <>
                        <dt className="text-slate-500">企業名/組織名</dt><dd>{value.company}</dd>
                        <dt className="text-slate-500">お名前</dt><dd>{value.userName}</dd>
                        <dt className="text-slate-500">システム名</dt><dd>{value.title}</dd>
                    </>
                )}
            {/* ご契約者の連絡先。行は API が作る（入っているものだけ）。プラン変更と無い版の API では出さない */}
            {(value.contractorLines ?? null) === null
                ? null
                : (
                    <>
                        <dt className="text-slate-500">ご連絡先</dt>
                        <dd data-contractor-lines="">
                            {value.contractorLines?.map((line, index) => (
                                <span key={`${String(index)}-${line}`} className="block">{line}</span>
                            ))}
                        </dd>
                    </>
                )}
            <dt className="text-slate-500">お支払方法</dt><dd>{value.paymentTypeLabel}</dd>
            <dt className="text-slate-500">月額（税込）</dt>
            <dd>
                <span className="text-lg font-semibold text-slate-900">{value.priceLabel}</span>
                {value.payDateLabel === null
                    ? null
                    : <span className="text-slate-500">（{value.payDateLabel}）</span>}
            </dd>
            {/* 申し込む前に長さを見せる。「初回のお支払い」だけだと、月初と月末で無料の長さが倍違うことに気付けない（Implem.Portal.Backend の docs/03 §2） */}
            {value.trialLabel === null
                ? null
                : (
                    <>
                        <dt className="text-slate-500">無料でお使いいただける期間</dt>
                        <dd>
                            <span className="badge-info">{value.trialLabel}</span>
                            <span className="text-slate-500">{trialNote}</span>
                        </dd>
                    </>
                )}
            {/*
              有償のときの契約の続き方と、やめ方（特定商取引法の最終確認画面の表示事項。Implem.Portal.Backend の docs/features/07 §1）。
              文面はよくあるご質問（解約・返金）と揃える。支払日が出ない（無料の）ときは出さない
            */}
            {value.payDateLabel === null
                ? null
                : (
                    <>
                        <dt className="text-slate-500">契約の更新</dt>
                        <dd>1 か月ごとに自動で更新されます。解約のお手続きをされるまで、毎月の月額料金がかかります。</dd>
                        <dt className="text-slate-500">解約</dt>
                        <dd>
                            契約情報の「解約」から、いつでもお手続きいただけます。
                            月の途中で解約された場合も、お支払い済みの料金の返金（日割りを含む）はありません。
                        </dd>
                    </>
                )}
        </dl>
    );
}

/** 確認。<b>確定の口は送り直した入力を改めて全部検証する</b>（ここに出した内容は根拠にしない）。 */
export function ConfirmationView({ flow, kind }: {
    readonly flow: ContractFlow;
    readonly kind: ContractKind;
}): ReactElement | null {
    usePageMeta(kind.confirmTitle);
    const renderCardCheckout = useContext(CardCheckoutContext);
    const value = flow.confirmation;
    if (value === undefined) {
        return null;
    }

    const onSubmit = (event: SubmitEvent<HTMLFormElement>): void => {
        event.preventDefault();
        if (!globalThis.confirm(kind.dialog(value))) {
            return;
        }
        flow.commit();
    };

    // カード払いは PAY.JP のボタンそのものを押す。<b>ブラウザの確認ダイアログは出さない</b>——PAY.JP の入力画面の
    // 送信（「申し込む」など）が最後の確認になる（docs/01 §7）。トークンが返ったらそのまま確定の口へ送る
    const onToken = (token: string): void => {
        flow.commitWithCard(token);
    };

    return (
        <div className="page max-w-2xl">
            <h1 className="page-title">{kind.confirmTitle}</h1>
            <p className="page-lead">{value.tenantTitle ?? kind.confirmLead}</p>

            <FormSummary messages={flow.summary} />

            <div className="card mb-4 p-6">
                <ConfirmationList value={value} trialNote={kind.trialNote} />
            </div>

            {kind.confirmNote === undefined
                ? null
                : <p className="alert-warning mb-4">{kind.confirmNote}</p>}

            {/* トークン化に失敗したときの理由。渡さないとカード拒否も 3-D セキュアの失敗も無反応になる */}
            {flow.cardError === undefined
                ? null
                : <div className="alert-danger mb-4" role="alert" data-payjp-error="">{flow.cardError}</div>}

            <form method="post" noValidate onSubmit={onSubmit}>
                <div className="flex gap-3">
                    <button type="button" className="btn-secondary" onClick={flow.back}>入力に戻る</button>
                    {value.requiresCard
                        ? renderCardCheckout({
                            publicKey: value.payjpPublicKey,
                            text: kind.cardButton,
                            submitText: kind.cardSubmitText,
                            disabled: flow.busy,
                            onOpen: () => { flow.setCardError(undefined); },
                            onToken,
                            onError: flow.setCardError,
                        })
                        : (
                            <button type="submit" className="btn-primary grow" disabled={flow.busy}>
                                {kind.submitButton}
                            </button>
                        )}
                </div>
            </form>
        </div>
    );
}
